/** Bracket / Swiss math shared by tournament UI.
 *  Must stay in sync with migoyugo-back-end/server/tournamentHandlers.js
 *  and tournamentSwiss.js
 */

export const MAX_TOURNAMENT_PLAYERS = 32;
export const MAX_SWISS_ROUNDS = 7;

export type TournamentFormat = 'single_elim' | 'swiss';

export function isSwissFormat(tournament?: { format?: string | null } | null): boolean {
  return tournament?.format === 'swiss';
}

export function suggestedSwissRounds(participantCount: number): number {
  if (participantCount < 2) return 0;
  return Math.ceil(Math.log2(participantCount));
}

export function clampSwissRounds(participantCount: number, requested?: number | null): number {
  if (participantCount < 2) return 0;
  const max = Math.min(MAX_SWISS_ROUNDS, Math.max(1, participantCount - 1));
  const parsed = requested == null ? NaN : parseInt(String(requested), 10);
  const fallback = suggestedSwissRounds(participantCount);
  const value = Number.isInteger(parsed) ? parsed : fallback;
  return Math.min(max, Math.max(1, value));
}

export function pairSwissRound1<T extends { seed?: number }>(participants: T[]): {
  pairings: { player1: T; player2: T }[];
  bye: T | null;
} {
  const players = [...participants].sort((a, b) => (a.seed || 0) - (b.seed || 0));
  let bye: T | null = null;
  if (players.length % 2 === 1) {
    bye = players.pop() || null;
  }
  const half = players.length / 2;
  const pairings: { player1: T; player2: T }[] = [];
  for (let i = 0; i < half; i++) {
    pairings.push({ player1: players[i], player2: players[i + half] });
  }
  return { pairings, bye };
}

function swissPairKey(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

function swissMatchPoints(match: {
  status: string;
  winner_id?: number | null;
  player1_id?: number | null;
  player2_id?: number | null;
}, userId: number): number {
  if (match.status === 'draw') return 0.5;
  if (match.status === 'double_forfeit') return 0;
  if (match.status === 'bye' && match.winner_id === userId) return 1;
  if ((match.status === 'complete' || match.status === 'walkover') && match.winner_id === userId) {
    return 1;
  }
  return 0;
}

export interface SwissStandingRow<T = { user_id: number; seed?: number; username?: string }> {
  user_id: number;
  seed?: number;
  username?: string;
  points: number;
  buchholz: number;
  hadBye: boolean;
  source: T;
}

export function computeSwissStandings<T extends { user_id: number; seed?: number; username?: string }>(
  participants: T[],
  matches: Array<{
    status: string;
    winner_id?: number | null;
    player1_id?: number | null;
    player2_id?: number | null;
  }>
): SwissStandingRow<T>[] {
  const byId = new Map<number, SwissStandingRow<T> & { opponents: number[] }>();
  for (const p of participants) {
    byId.set(p.user_id, {
      user_id: p.user_id,
      seed: p.seed,
      username: p.username,
      points: 0,
      buchholz: 0,
      hadBye: false,
      opponents: [],
      source: p
    });
  }
  const scored = ['complete', 'walkover', 'double_forfeit', 'bye', 'draw'];
  for (const match of matches) {
    if (!scored.includes(match.status)) continue;
    const p1 = match.player1_id ? byId.get(match.player1_id) : undefined;
    const p2 = match.player2_id ? byId.get(match.player2_id) : undefined;
    if (p1 && match.player1_id) p1.points += swissMatchPoints(match, match.player1_id);
    if (p2 && match.player2_id) p2.points += swissMatchPoints(match, match.player2_id);
    if (match.status === 'bye' && match.winner_id) {
      const byePlayer = byId.get(match.winner_id);
      if (byePlayer) byePlayer.hadBye = true;
    }
    if (match.player1_id && match.player2_id) {
      if (p1) p1.opponents.push(match.player2_id);
      if (p2) p2.opponents.push(match.player1_id);
    }
  }
  const rows = Array.from(byId.values());
  for (const row of rows) {
    row.buchholz = row.opponents.reduce((sum: number, oppId: number) => sum + (byId.get(oppId)?.points || 0), 0);
  }
  return rows.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.buchholz !== a.buchholz) return b.buchholz - a.buchholz;
    return (a.seed || 0) - (b.seed || 0);
  });
}

export function formatSwissPoints(points: number): string {
  if (Number.isInteger(points)) return String(points);
  return points.toFixed(1);
}

export function nextPowerOfTwo(n: number): number {
  let p = 1;
  while (p < n) p *= 2;
  return p;
}

export function seededBracketPositions(bracketSize: number): number[] {
  function order(n: number): number[] {
    if (n <= 1) return [1];
    const prev = order(n / 2);
    const out: number[] = [];
    for (const p of prev) {
      out.push(p);
      out.push(n + 1 - p);
    }
    return out;
  }
  return order(bracketSize);
}

export interface BracketPreviewPlayer {
  user_id: number;
  username?: string;
  seed?: number;
  elo_rating?: number;
  elo_fast?: number;
  elo_standard?: number;
}

export interface BracketPairing<T = BracketPreviewPlayer> {
  player1: T | null;
  player2: T | null;
}

export function generateBracket<T extends BracketPreviewPlayer>(
  participants: T[],
  bracketSize: number
): BracketPairing<T>[] {
  const bySeed = new Map<number, T>();
  const withSeeds = participants.filter(p => (p.seed || 0) >= 1 && (p.seed || 0) <= bracketSize);
  if (withSeeds.length === participants.length && withSeeds.length > 0) {
    for (const p of withSeeds) bySeed.set(p.seed as number, p);
  } else {
    const seeded = [...participants].sort((a, b) => (a.seed || 0) - (b.seed || 0));
    seeded.forEach((p, i) => { bySeed.set(i + 1, p); });
  }

  const positions = seededBracketPositions(bracketSize);
  const slots = positions.map(seedNum => bySeed.get(seedNum) || null);
  const pairings: BracketPairing<T>[] = [];
  for (let i = 0; i < slots.length; i += 2) {
    pairings.push({ player1: slots[i], player2: slots[i + 1] });
  }
  return pairings;
}

function seedRating(player: BracketPreviewPlayer, seedPool: 'fast' | 'standard' = 'standard'): number {
  if (seedPool === 'fast') {
    return player.elo_fast != null ? player.elo_fast : (player.elo_rating || 1200);
  }
  return player.elo_standard != null ? player.elo_standard : (player.elo_rating || 1200);
}

export function compactParticipantSeeds<T extends BracketPreviewPlayer>(
  participants: T[],
  seedPool: 'fast' | 'standard' = 'standard'
): T[] {
  const list = participants.map(p => ({ ...p }));
  const assigned = list.filter(p => (p.seed || 0) >= 1).sort((a, b) => (a.seed || 0) - (b.seed || 0));
  const unassigned = list.filter(p => !p.seed || p.seed < 1)
    .sort((a, b) => seedRating(b, seedPool) - seedRating(a, seedPool));
  const ordered = [...assigned, ...unassigned];
  ordered.forEach((p, i) => { p.seed = i + 1; });
  return ordered;
}

export function assignSeedsForPreview<T extends BracketPreviewPlayer>(
  participants: T[],
  seedPool: 'fast' | 'standard' = 'standard'
): T[] {
  return compactParticipantSeeds(participants, seedPool);
}

export function previewTournamentBracket<T extends BracketPreviewPlayer>(
  participants: T[],
  seedPool: 'fast' | 'standard' = 'standard'
) {
  if (!participants || participants.length < 2) {
    return { bracketSize: 0, totalRounds: 0, pairings: [] as BracketPairing<T>[], seeded: [] as T[], byeCount: 0 };
  }
  const seeded = assignSeedsForPreview(participants, seedPool);
  const bracketSize = nextPowerOfTwo(seeded.length);
  const pairings = generateBracket(seeded, bracketSize);
  const byeCount = pairings.filter(p =>
    (p.player1 && !p.player2) || (!p.player1 && p.player2)
  ).length;
  return {
    bracketSize,
    totalRounds: getTotalRoundsFromParticipantCount(seeded.length),
    pairings,
    seeded,
    byeCount
  };
}

export function getTotalRoundsFromParticipantCount(participantCount: number): number {
  if (participantCount < 2) return 0;
  return Math.log2(nextPowerOfTwo(participantCount));
}

export function getTotalRoundsFromBracketSize(bracketSize: number): number {
  if (bracketSize < 2) return 0;
  return Math.log2(bracketSize);
}

export function formatTournamentDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function emptyBracketSlotLabel(status?: string | null): string {
  return status === 'bye' ? 'Bye' : 'TBD';
}

export function bracketPlayerLabel(
  username?: string | null,
  playerId?: number | null,
  status?: string | null
): string {
  if (username) return username;
  if (playerId) return `Player #${playerId}`;
  return emptyBracketSlotLabel(status);
}

export function tournamentRoundLabel(
  currentRound?: number | null,
  totalRounds?: number | null,
  format?: string | null
): string | null {
  if (!totalRounds) return null;
  if (!currentRound) {
    return format === 'swiss'
      ? 'Pairings set — waiting to open Round 1'
      : 'Bracket set — waiting to open Round 1';
  }
  return `Round ${currentRound} of ${totalRounds}`;
}

export function visibleRoundSettings<T extends { round: number }>(
  settings: T[],
  totalRounds: number
): T[] {
  if (!totalRounds) return settings;
  return settings.filter(s => s.round >= 1 && s.round <= totalRounds);
}

export function canEnableHardAutoStart(
  participantCount: number,
  roundSettings: { round: number }[],
  scheduledStartAt?: string | null,
  options?: { format?: string | null; plannedRounds?: number | null }
): { ok: boolean; reason?: string } {
  if (!scheduledStartAt) return { ok: false, reason: 'Set a scheduled start time first' };
  if (participantCount < 2) return { ok: false, reason: 'Need at least 2 players' };
  const total = options?.format === 'swiss'
    ? clampSwissRounds(participantCount, options.plannedRounds)
    : getTotalRoundsFromParticipantCount(participantCount);
  if (total === 0) return { ok: false, reason: 'Need at least 2 players' };
  const allSet = Array.from({ length: total }, (_, i) => i + 1)
    .every(r => roundSettings.some(s => s.round === r));
  if (!allSet) return { ok: false, reason: 'All round time controls must be set' };
  return { ok: true };
}

export function defaultDateTimeLocalValue(minutesFromNow = 60): string {
  const d = new Date(Date.now() + minutesFromNow * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
